import React from 'react'
import axios from 'axios'
import * as yup from 'yup'
import { Grid, Button, Pagination } from '@softwaredev_cegeka/stix-core'
import { Fieldset, FormGroup, Info } from '@softwaredev_cegeka/stix-form'

import { AuthApi } from 'src/api/authApi'
import BaseForm from 'src/components/Forms/BaseForm'
import AsyncSelect from 'src/components/Forms/AsyncSelect'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import AppPermissionsTable from 'src/components/EntityTable/AppPermissionsTable'
import Popover from '@softwaredev_cegeka/stix-core/dist/components/Popover'

class AppPermissionsForm extends BaseForm {
    pages = [
        {
            renderMethod: this.renderForm,
            validationSchema: yup.object().shape({
                application: yup
                    .object()
                    .shape({
                        id: yup.number(),
                        label: yup.string(),
                        value: yup.string(),
                    })
                    .required('Please insert the name of the application')
                    .typeError('Please insert the name of the application'),
                tenant: yup
                    .array()
                    .of(
                        yup.object().shape({
                            id: yup.mixed(),
                            label: yup.string(),
                            value: yup.string(),
                        })
                    )
                    .required('Please select a tenant')
                    .typeError('Please select a tenant'),
            }),
        },
    ]

    initialValues = {
        application: null,
        tenant: null,
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    constructor(props, context) {
        super(props, context)
        this.state.values = {
            ...this.initialValues,
            appPermissionCreated: null,
            service: null,
            serviceActionsView: true,
            checkedActions: [],
        }
        this.state = {
            ...this.state,
            actions: [],
            allActions: [],
            page: 1,
            per_page: 10,
            loading: false,
            addServiceActionsView: false,
            totalPages: 1,
            params: {},
        }
        notifierStore.setShowMessage(false)
        this.authClient
            .fetchGroup(this.props.match.params.id)
            .then(response => {
                this.setState((state, _) => ({
                    values: {
                        ...state.values,
                        groupName: response.data.name,
                        tenant_id: response.data.tenant_id,
                        organization_id: response.data.organization_id,
                    },
                }))

                return this.authClient.fetchTenant(response.data.tenant_id)
            })
            .then(response => {
                this.setState((state, _) => ({
                    values: {
                        ...state.values,
                        tenant_name: response?.data?.name,
                        tenant_company_id: response?.data?.company_id,
                        tenant_is_company_owner:
                            response?.data?.is_company_owner,
                    },
                }))
            })
            .finally(() => {
                if (props.match.params.permissionId) {
                    this.authClient
                        .fetchAppPermission(
                            props.match.params.id,
                            props.match.params.permissionId
                        )
                        .then(response => {
                            this.setState(
                                (state, _) => ({
                                    values: {
                                        ...state.values,
                                        application: {
                                            id: response?.data?.application_id,
                                            value: response?.data?.application,
                                            label: response?.data?.application,
                                        },
                                        tenant:
                                            response?.data?.tenants.length === 0
                                                ? [
                                                      {
                                                          id: 1,
                                                          label: state.values
                                                              .tenant_is_company_owner
                                                              ? 'All'
                                                              : 'All (parent and subsidiaries)',
                                                          value: 'all',
                                                      },
                                                  ]
                                                : response?.data?.tenants.map(
                                                      item => ({
                                                          id: item.id,
                                                          label: item.name,
                                                          value: item.name,
                                                      })
                                                  ),
                                        checkedActions:
                                            response?.data?.service_actions,
                                    },
                                }),
                                () => {
                                    this.fetchActions()
                                    this.fetchAllActions()
                                }
                            )
                        })
                } else {
                    this.setState((state, _) => ({
                        values: {
                            ...state.values,
                            tenant: [
                                {
                                    id: this.state.values.tenant_id,
                                    label: this.state.values.tenant_name,
                                    value: this.state.values.tenant_name,
                                },
                            ],
                        },
                    }))
                }
            })
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    generateGeneralInfoPayload = () => {
        const values = this.state.values
        let payload = {
            application_id: values.application.id.toString(),
            service_action_ids: values.checkedActions
                ? values.checkedActions.map(item => item.id.toString())
                : [],
        }
        return this.addTenantToPayload(payload)
    }

    submitGeneralInfo = async () => {
        let isValid = await this.validateForm()
        if (isValid) {
            const payload = this.generateGeneralInfoPayload()
            if (!payload) {
                console.warn('No data to submit')
                console.info('state values: ', this.state.values)
                return
            }
            notifierStore.setShowMessage(false)
            try {
                let resp = await axios({
                    url: this.getSubmitUrl(),
                    method: this.getSubmitMethod(),
                    data: payload,
                })
                if (resp.status === 200) {
                    notifierStore.setToastInfo(
                        this.props.match.params.permissionId
                            ? 'App permission data successfully changed'
                            : 'App permission successfully created',
                        true
                    )
                    this.setState((state, _) => ({
                        values: {
                            ...state.values,
                            appPermissionCreated: resp.data.id,
                        },
                    }))
                    this.fetchActions(1)
                } else {
                    notifierStore.setToastInfo(
                        this.props.match.params.permissionId
                            ? 'App permission could not be updated'
                            : 'App permission could not be created',
                        false
                    )
                }
            } catch (ex) {
                console.warn('API Exception', ex)
                notifierStore.setToastInfo(
                    ex.response?.data?.error || ex.response?.data?.detail,
                    false
                )
            } finally {
                notifierStore.setShowMessage(true)
            }
        } else {
            this.activateFirstPageWithErrors()
        }
    }

    generateActionsPayload = selected => {
        const values = this.state.values
        let payload = {
            application_id: values.application.id.toString(),
            service_action_ids: selected.map(String),
        }
        return this.addTenantToPayload(payload)
    }

    addTenantToPayload = payload => {
        const { values } = this.state
        if (!values.tenant) {
            return payload
        }
        if (
            (values.tenant.length >= 1 &&
                values.tenant.map(item => item.value).includes('all')) ||
            values.tenant.value === 'all'
        ) {
            return {
                ...payload,
                tenant_ids: 'all',
            }
        }
        return {
            ...payload,
            tenant_ids: values.tenant.map(item => item.id.toString()),
        }
    }

    submitAppPermissionActions = async selected => {
        let isValid = await this.validateForm()
        if (isValid) {
            const payload = this.generateActionsPayload(selected)
            if (!payload) {
                console.warn('No data to submit')
                console.info('state values: ', this.state.values)
                return
            }
            notifierStore.setShowMessage(false)
            try {
                let resp = await axios({
                    url: this.getSubmitActionsUrl(),
                    method: 'PUT',
                    data: payload,
                })
                if (resp.status === 200) {
                    notifierStore.setToastInfo(
                        'Actions successfully (un)linked to app permission',
                        true
                    )
                    this.setState((state, _) => ({
                        values: {
                            ...state.values,
                            checkedActions: resp.data?.service_actions,
                        },
                    }))
                } else {
                    notifierStore.setToastInfo(
                        'Actions could not be updated',
                        false
                    )
                }
            } catch (ex) {
                console.warn('API Exception', ex)
                notifierStore.setToastInfo(
                    ex.response?.data?.error || ex.response?.data?.detail,
                    false
                )
            } finally {
                notifierStore.setShowMessage(true)
            }
        } else {
            this.activateFirstPageWithErrors()
        }
    }

    getSubmitUrl() {
        if (this.props.match.params.permissionId) {
            return `/api/auth/groups/${this.props.match.params.id}/app_permissions/${this.props.match.params.permissionId}`
        }
        return `/api/auth/groups/${this.props.match.params.id}/app_permissions`
    }

    getSubmitMethod() {
        if (this.props.match.params.permissionId) {
            return 'PUT'
        }
        return 'POST'
    }

    getSubmitActionsUrl() {
        const permissionId =
            this.state.values.appPermissionCreated ||
            this.props.match.params.permissionId
        return `/api/auth/groups/${this.props.match.params.id}/app_permissions/${permissionId}`
    }

    getSuccessMessage() {
        if (this.props.match.params.permissionId) {
            return 'App permission successfully updated'
        }
        return 'App permission successfully created'
    }

    getFailureMessage() {
        if (this.props.match.params.permissionId) {
            return 'App permission could not be modified'
        }
        return 'App permission could not be created'
    }

    getTitleText() {
        if (this.props.match.params.permissionId) {
            return (
                'Edit app permission for group ' + this.state.values.groupName
            )
        }
        return 'Create app permission for group ' + this.state.values.groupName
    }

    // overwrite this in order to render the buttons separately
    renderFormActions(pageNumber) {
        return null
    }

    handleTenantRender = response => {
        let res = [
            {
                id: 'all',
                value: 'all',
                label: this.state.values.tenant_is_company_owner
                    ? 'All'
                    : 'All (parent and subsidiaries)',
            },
            {
                id: this.state.values.tenant_id,
                value: this.state.values.tenant_name,
                label: this.state.values.tenant_name,
            },
        ]
        const potentialChildTenants = response.data.results
            .filter(item => item.id !== this.state.values.tenant_id)
            .filter(
                item => item.company_id === this.state.values.tenant_company_id
            )
            .map(item => ({
                id: item.id,
                value: item.name,
                label: `${item.name}`,
            }))

        // We only want to show the "All" option if there is more than one tenant
        if (potentialChildTenants.length === 0) {
            res.shift()
        }

        return res.concat(potentialChildTenants)
    }

    handleApplicationRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.key,
            label: `${item.key} - ${item.description}`,
        }))
    }

    fetchGroupAppPermissions = (
        page = this.state.page,
        groupId,
        permissionId
    ) => {
        this.state = {
            ...this.state,
            loading: true,
        }
        let appPermissionId =
            this.state.values.AppPermissionCreated || permissionId
        this.authClient
            .fetchAppPermissions(page, groupId, appPermissionId)
            .then(response => {
                this.setState({
                    appPermissions: response?.data?.service_actions,
                    loading: false,
                    group: response?.data?.group,
                    tenants: response?.data?.tenants,
                    application: response?.data?.application,
                    organization: response?.data?.tenants[0]?.name,
                    totalPages: Math.round(
                        response?.data?.service_actions.length / 10
                    ),
                    paginatedPermissions: response?.data?.service_actions.slice(
                        page - 1,
                        10
                    ),
                })
            })
    }

    fetchActions = (page = this.state.page) => {
        let applicationId = this.state.values.application.id
        this.state = {
            ...this.state,
            loading: true,
        }
        this.authClient
            .fetchApplicationActions(applicationId, page, {
                ...this.state.params,
                load_relationships: true,
            })
            .then(response => {
                this.setState({
                    actions: response?.data?.results,
                    totalPages: response?.data?._page.total_pages,
                    loading: false,
                })
            })
    }

    fetchAllActions = (page = 1) => {
        let applicationId = this.state.values.application.id
        this.state = {
            ...this.state,
            loading: true,
        }
        this.authClient
            .fetchApplicationActions(applicationId, page, {
                ...this.state.params,
                load_relationships: true,
                per_page: 1000,
            })
            .then(response => {
                this.setState({
                    allActions: response?.data?.results,
                    loading: false,
                })
            })
    }

    setParams = params => {
        this.setState(
            {
                params: params,
            },
            () => this.fetchActions()
        )
    }

    removeAppPermission = () => {
        try {
            this.authClient
                .removeAppPermission(
                    this.props.match.params.id,
                    this.props.match.params.permissionId
                )
                .then(response => {
                    if (response.status === 204) {
                        notifierStore.setToastInfo(
                            'App permission successfully deleted',
                            true
                        )
                    } else {
                        notifierStore.setToastInfo(
                            'App permission could not be deleted',
                            false
                        )
                    }
                })
        } catch (ex) {
            console.warn('API Exception', ex)
            notifierStore.setToastInfo(
                ex.response?.data?.error || ex.response?.data?.detail,
                false
            )
        } finally {
            notifierStore.setShowMessage(true)
            this.setState({
                submittedSuccessfully: true,
            })
        }
    }

    renderConfirmationModal = () => {
        return (
            <div className="modal-wrapper">
                <div>Are you sure you want to delete this app permission?</div>
                <div className="button-modal-wrapper">
                    <Button
                        variant={'outline-danger'}
                        onClick={() => this.removeAppPermission()}
                    >
                        Confirm
                    </Button>
                </div>
            </div>
        )
    }

    handlePageChange = page => {
        this.setState({ page: page }, () => this.fetchActions())
    }

    renderForm() {
        return (
            <>
                <div className={'header-form-wrapper'}>
                    <h1>{this.getTitleText()}</h1>
                    {this.props.match.params.permissionId && (
                        <div>
                            <Popover
                                rootClose={true}
                                placement="left"
                                trigger="click"
                                content={this.renderConfirmationModal()}
                            >
                                <Button size={'sm'} variant="danger">
                                    Delete
                                </Button>
                            </Popover>
                        </div>
                    )}
                </div>
                <Fieldset title="Application info">
                    <Grid.Row>
                        <Grid.Col>
                            <FormGroup>
                                <AsyncSelect
                                    {...this.getCommonSelectProps(
                                        'application'
                                    )}
                                    label="Application"
                                    apiConfig={{
                                        url: '/api/auth/applications',
                                        params: inputValue => ({
                                            name_like: inputValue,
                                            per_page: 100,
                                        }),
                                    }}
                                    itemRenderer={this.handleApplicationRender}
                                    required
                                    search
                                    disabled={
                                        this.props.match.params.permissionId ||
                                        this.state.createdEntityId
                                    }
                                />
                            </FormGroup>
                        </Grid.Col>
                        <Grid.Col>
                            <FormGroup>
                                <AsyncSelect
                                    {...this.getCommonSelectProps('tenant')}
                                    label="Tenant"
                                    apiConfig={{
                                        url: '/api/auth/tenants',
                                        params: inputValue => {
                                            return {
                                                name_like: inputValue,
                                                per_page: 100,
                                                ...(this.state.values
                                                    .tenant_is_company_owner ??
                                                false
                                                    ? {}
                                                    : {
                                                          parent_tenant_id:
                                                              this.state.values
                                                                  .tenant_id,
                                                      }),
                                            }
                                        },
                                    }}
                                    itemRenderer={this.handleTenantRender}
                                    required
                                    isMulti
                                    search
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col>
                            <Button
                                className={'mb-3'}
                                size={'sm'}
                                key="submit"
                                variant="secondary"
                                onClick={this.submitGeneralInfo}
                                disabled={
                                    this.state.values.appPermissionCreated &&
                                    !this.props.match.params.id
                                }
                            >
                                Submit
                            </Button>
                        </Grid.Col>
                    </Grid.Row>
                </Fieldset>
                <Fieldset title="App permissions">
                    <Info
                        info={
                            'You need to first submit the general info in order to be able to link actions to the app permission'
                        }
                    />
                    <AppPermissionsTable
                        {...this.props}
                        data={this.state.actions}
                        checkedActions={this.state.values.checkedActions}
                        loading={this.state.loading}
                        params={this.state.params}
                        setParams={this.setParams}
                        canEdit={true}
                        allActions={this.state.allActions}
                        saveAppPermissions={this.submitAppPermissionActions}
                        displayNamespace
                    />

                    {this.state.totalPages >= 2 && (
                        <div className="pagination-wrapper">
                            <Pagination
                                lastPage={this.state.totalPages}
                                currentPage={this.state.page}
                                onClick={
                                    this.state.loading
                                        ? () => {}
                                        : this.handlePageChange
                                }
                            />
                        </div>
                    )}
                </Fieldset>
            </>
        )
    }
}

export default AppPermissionsForm
